/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import ExitIcon from 'components/Icons/Exit/Exit';
import useImages from './images';
import './LightBox.scss';

/**
 * This is the LightBox component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function LightBox({ selector }) {
  const container = useRef(null);
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState(null);
  const [windowRatio, setWindowRatio] = useState(
    window.innerHeight / window.innerWidth
  );
  const [imageRatio, setImageRatio] = useState(9 / 16);
  const images = useImages(selector);

  useEffect(() => {
    function onResize() {
      setWindowRatio(window.innerHeight / window.innerWidth);
    }

    window.addEventListener('resize', onResize);

    return function cleanup() {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    function onClick({ target }) {
      setSrc(target.src);
      setOpen(true);
    }

    images.forEach(image => {
      image.classList.add('zoom');
      image.addEventListener('click', onClick);
    });

    return function cleanup() {
      images.forEach(image => {
        image.classList.remove('zoom');
        image.removeEventListener('click', onClick);
      });
    };
  }, [images]);

  function onBackgroundClick({ target }) {
    if (target === container.current) {
      setOpen(false);
    }
  }

  function onImageLoad({ target }) {
    setImageRatio(target.offsetHeight / target.offsetWidth);
  }

  function getWidth() {
    if (imageRatio > windowRatio) {
      return `${80 * imageRatio}%`;
    }

    return '80%';
  }

  return (
    <CSSTransition in={open} timeout={500}>
      <div ref={container} className="light-box" onClick={onBackgroundClick}>
        <div className="box" style={{ width: getWidth() }}>
          <button
            onClick={() => {
              setOpen(false);
            }}
          >
            <ExitIcon />
          </button>
          <img src={src} onLoad={onImageLoad} />
        </div>
      </div>
    </CSSTransition>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
LightBox.propTypes = {
  selector: PropTypes.string.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
LightBox.defaultProps = {
  selector: 'img',
};

export default LightBox;
